import type { BadgeVariants } from "~/components/ui";

export enum ANALYTICS_SOURCE {
    BOM = "BOM",
    BOM_TOOL = "BOMTool",
    EMBEDDABLE_BOM_TOOL = "EmbeddableBOM",
    LEGACY_DSS = "API",
    GLOBAL = "GlobalWebsite",
    API_V1 = "InventoryAPI",
    API_V2 = "APIv2",
    DSS = "DSS",
    PART_CATEGORY = "PartCategory",
    PART_DETAIL = "PartDetail",
    PRODUCT_SPOTLIGHT = "ProductSpotlight",
    PRODUCT_SERIES = "ProductSeries",
    WEBSITE = "Website",
}

export enum MY_ACCOUNT_PAGES {
    PROFILE = "profile",
    BOMS = "boms",
    NOTIFICATIONS = "notifications",
    NOTIFICATION_EMAILS = "notification-emails",
    CHANGE_PASSWORD = "change-password",
    API_KEY = "api-key",
    ADDITIONAL_FEATURES = "additional-features",
}

export enum Modifier {
    LessThan = 1,
    GreaterThan = 2,
    LessThanOrEqual = 3,
    GreaterThanOrEqual = 4,
    Equal = 5,
}

export enum AlertStatus {
    Enabled = 1,
    Disabled = 2,
    Deleted = 3,
}

export const BOM_TOOL = {
    LOCK_POLL_MS: 60_000, // 1 minute
    STAGGER_PART_SEARCHES_MS: 500,
    SORT_ORDER: {
        Custom: 1,
        PartNum_Asc: 2,
        PartNum_Desc: 3,
        Manufacturer_Asc: 4,
        Manufacturer_Desc: 5,
    },
    DIRECT_DOWNLOAD_MAX_PARTS: 250,
    STATUS_CHECK_DELAY_MS: 1_000,
    DEFAULT_PAGE_SIZE: 50,
    BATCH_SIZE: 5,
    AUTHENTICATED_BATCH_SIZE: 20,
    MAX_PARTS: 100,
    MAX_EXTENDED_PARTS: 1000,
    EMISSIONS_MAX_LENGTH: 6,
    STORAGE_KEY: "ecia-bom-anonymous",
    MAX_BOMS_PER_USER: 100,
} as const;

export const PAGE_SIZES = [10, 20, 50] as const;

export const SEARCH = {
    MAX_DESCRIPTION_LENGTH: 200,
    DROPDOWN_TYPE: "dropdown",
    MULTI_TYPE: "multi",
};

export const SCREEN_BREAKPOINTS = {
    sm: 768,
    md: 992,
    lg: 1200,
};

export const LANGUAGE_CODES = ["de", "en", "es", "fr", "it", "pt", "ja", "zh-hans", "zh-hant"];

export enum Role {
    Admin = 1,
    Distributor = 2,
    Manufacturer = 3,
    ManufacturerInventoryExport = 4,
    ExtendedBom = 5,
    MemberAdmin = 6,
    ReportingAdmin = 7,
    StockCheck = 8,
    TechnicalAdmin = 9,
    Translator = 10,
    ApiCustomer = 11,
    ContentAdmin = 12,
    LeadTimeWeekly = 13,
    LeadTimeMonthly = 14,
    SluiceboxAccess = 15,
}

export type RiskOption = {
    label: string;
    variant: NonNullable<BadgeVariants["variant"]>;
    fill: NonNullable<BadgeVariants["fill"]>;
    tooltip: string;
};

export const LIFECYCLE_RISK_OPTIONS: Record<number, RiskOption> = {
    0: {
        label: "Global.RiskLevelLow",
        variant: "success",
        fill: "dark",
        tooltip: "Global.LifecycleRiskLevelLowToolTip",
    },
    1: {
        label: "Global.RiskLevelLowMedium",
        variant: "success",
        fill: "light",
        tooltip: "Global.LifecycleRiskLevelLowMediumToolTip",
    },
    2: {
        label: "Global.RiskLevelMedium",
        variant: "warning",
        fill: "dark",
        tooltip: "Global.LifecycleRiskLevelMediumToolTip",
    },
    3: {
        label: "Global.RiskLevelHigh",
        variant: "danger",
        fill: "dark",
        tooltip: "Global.LifecycleRiskLevelHighToolTip",
    },
};

export const SUPPLYCHAIN_RISK_OPTIONS: Record<number, RiskOption> = {
    0: {
        label: "Global.RiskLevelLow",
        variant: "success",
        fill: "dark",
        tooltip: "Global.SupplyChainRiskLevelLowToolTip",
    },
    1: {
        label: "Global.RiskLevelLowMedium",
        variant: "success",
        fill: "light",
        tooltip: "Global.SupplyChainRiskLevelLowMediumToolTip",
    },
    2: {
        label: "Global.RiskLevelMedium",
        variant: "warning",
        fill: "dark",
        tooltip: "Global.SupplyChainRiskLevelMediumToolTip",
    },
    3: {
        label: "Global.RiskLevelHigh",
        variant: "danger",
        fill: "dark",
        tooltip: "Global.SupplyChainRiskLevelHighToolTip",
    },
};

export const REGION_SHORT_NAMES = {
    1: "AMERICAS",
    2: "CHINA",
    3: "APAC",
    4: "EMEA",
    5: "JAPAN",
};

// Source Types PriceAlert
export const MODIFIER_TYPE = {
    LESS_THAN: 1,
    GREATER_THAN: 2,
};

export const PART_MODEL_STATUS = {
    Unknown: -1,
    NotFound: 0,
    ModelsExist: 1,
};

export const MIN_SEARCH_LENGTH = 2;
export const CONTINUE_AS_GUEST_FLAG = "ecia-continue-as-guest";
export const CONTINUE_AS_GUEST_EXPIRATION = 90;
export const MAX_FILE_UPLOAD_MEGABYTES = 50;

export enum FeatureRequestTypes {
    MANUFACTURER_PORTAL = 1,
    DISTRIBUTOR_PORTAL = 2,
    EXTENDED_BOM = 3,
    API_DSS = 4,
    LEAD_TIME_REPORTING = 5,
    CARBON_FOOTPRINT = 6,
}

export enum AuthorizationStatus {
    Authorized = "A",
    Unauthorized = "U",
    NotSet = "N",
}

export enum AuthorizationProofType {
    CoverLetter = 0,
    ManufacturerEmail = 1,
    ManufacturerLink = 2,
    ManufacturerSet = 3,
}

export const ROW_DELETE_DELAY_MS = 5_000;
export const TOAST_DURATION_MS = 10_000;
